import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import Header from '../Header';
import Sidebar from '../Sidebar';
import QuickActionsFloatingBar from '../QuickActionsFloatingBar';
import CommandPalette, { CommandPaletteProvider } from '../CommandPalette';
import {
  SidebarDensityProvider,
  useSidebarDensity,
} from '../sidebar/density';
import Breadcrumb from './Breadcrumb';
import { BreadcrumbProvider } from './BreadcrumbContext';
import { useAuth } from '../../context/AuthContext';
import { creditsAPI } from '../../services/api';

// Routes where the breadcrumb adds nothing useful — the sidebar + page
// title already say "you're on Dashboard". Keep the list tight; in
// general the breadcrumb is on.
const BREADCRUMB_HIDDEN_PATHS = ['/dashboard'];

const AuthenticatedLayoutInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { density, toggleDensity } = useSidebarDensity();
  const mainRef = useRef<HTMLElement>(null);

  const hideLayout = ['/reset-password-confirm'].some((path) =>
    location.pathname.startsWith(path)
  );

  // Scroll the main content area to top when route changes (e.g. Admin → Feedbacks → Tickets)
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!user) {
      setSubscriptionStatus(null);
      return;
    }
    creditsAPI.getBalance().then((data) => {
      setSubscriptionStatus(data?.subscription_status ?? null);
    }).catch(() => setSubscriptionStatus(null));
  }, [user, location.pathname]);

  // Sidebar density toggle — `[` flips full ↔ rail. Skip when the user
  // is typing in an input / textarea / contenteditable so we don't eat
  // the keystroke in normal forms.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      e.preventDefault();
      toggleDensity();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggleDensity]);

  if (hideLayout) {
    return <>{children}</>;
  }

  const showPastDueBanner = subscriptionStatus === 'past_due';

  // Margin matches sidebar width: 232 px (full) or 60 px (rail). Below
  // the lg breakpoint the sidebar is a drawer over the content, so no
  // margin is needed.
  const mainMarginClass = density === 'rail' ? 'lg:ml-[60px]' : 'lg:ml-[232px]';
  const sidebarWidth = density === 'rail' ? '60px' : '232px';
  // PageShell + every page that hand-rolls `calc(100vw - 14rem)` reads
  // this CSS variable so the inner content tracks the live sidebar
  // width. Fallback in PageShell preserves legacy 14rem behaviour.
  const layoutStyle = { '--sidebar-w': sidebarWidth } as React.CSSProperties;

  return (
    <div className="flex h-screen overflow-hidden bg-surface-bg" style={layoutStyle}>
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className={`flex min-h-0 min-w-0 flex-1 flex-col ${mainMarginClass}`}>
        <Header setIsOpen={setSidebarOpen} />
        <main
          ref={mainRef}
          className="min-h-0 min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto scrollbar-hide p-0 pt-16"
          data-tour="page-content"
        >
          {showPastDueBanner && (
            <div
              role="alert"
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-warn-tint border-b border-warn-border text-warn-text text-sm"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>Payment failed. Please update your payment method to avoid service interruption.</span>
              <button
                type="button"
                onClick={() => navigate('/subscription')}
                className="ml-2 font-semibold text-amber-300 hover:text-amber-200 underline underline-offset-2"
              >
                Update payment
              </button>
            </div>
          )}
          <Breadcrumb hiddenOnPaths={BREADCRUMB_HIDDEN_PATHS} />
          {children}
        </main>
      </div>
      <QuickActionsFloatingBar />
      <CommandPalette />
    </div>
  );
};

const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <CommandPaletteProvider>
    <SidebarDensityProvider>
      <BreadcrumbProvider>
        <AuthenticatedLayoutInner>{children}</AuthenticatedLayoutInner>
      </BreadcrumbProvider>
    </SidebarDensityProvider>
  </CommandPaletteProvider>
);

export default AuthenticatedLayout;
